import { Order } from "@sb1finans/new-api/dist/api"
import { Typography, Box, Stepper, Step, StepLabel, StepContent, TextField, Button, Stack } from "@mui/material"
import CompanySearch from "@sb1finans/components/dist/CompanySearch"
import styled from "@emotion/styled"
import { useDropzone } from "react-dropzone"
import { FilePresent } from "@mui/icons-material"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import React, { useContext, useState } from "react"
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { toBase64 } from "@sb1finans/components/dist/Utils"
import { v4 as uuid } from 'uuid';
import Paper from "@mui/material/Paper"
import { OrderContext } from "../providers/OrderProvider"
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from "@mui/material/Alert"
import { AgreementContext } from "@sb1finans/components/dist/AgreementContext"
import { min, Moment } from "moment"

const PageContainer = styled(Paper)`
    max-width: 800px;
    margin: 0 auto;
`

const Dropzone = styled.div`
    padding: 20px;
    border-radius: 2px;
    border: ${(props: any) => props.isEmpty ? "2px dashed #bdbdbd" : "2px dashed #333"};
    color: ${(props: any) => props.isEmpty ? "#bdbdbd" : "#333"};;
`;

const FileNameContainer = styled.div`
    display: flex;
    align-items: center;
`

interface OrderDraft {
    debtorOrgNo?: string,
    debtorName?: string,
    attachments?: any[],
    orderNumber?: number,
    amount?: string,
    orderDate?: string,
    dueDate?: string,
    contactName?: string,
    contactEmail?: string,
    contactPhone?: string,
    approverName?: string,
    approverEmail?: string,
    isEhf?: boolean
}

function isNumeric(str: string) {
    if (typeof str != "string") return false // we only process strings!  
    return !isNaN(parseInt(str)) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
           !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
}

const mapData = (draft: OrderDraft, agreementId: string): Order => {
    if (!draft.orderNumber) throw Error("Order number is required")
    if (!draft.amount) throw Error("Amount is required")
    if (!draft.orderDate) throw Error("Order date is required")
    if (!draft.dueDate) throw Error("Due date is required")
    if (!isNumeric) throw Error("Amount must be numeric")
    if (!draft?.attachments || draft.attachments.length == 0) throw Error("Invoice file can not be empty.")

    const orderNumber = draft.orderNumber
    const amount = parseInt(draft.amount) * 100

    const contactNames = draft.contactName?.split(" ") ?? []
    const contactFirstName = contactNames.slice(0, contactNames.length - 1).reduce((acc, curr) => `${acc} ${curr}`, "").trim()
    const contactLastName = contactNames[contactNames.length - 1]

    /*const contactNames = draft.contactName?.split(" ") ?? []
    const contactFirstName = contactNames.slice(0, contactNames.length - 1).reduce((acc, curr) => `${acc} ${curr}`, "").trim()
    const contactLastName = contactNames[contactNames.length - 1]*/

    
    return {
        id: uuid(),
        agreementId: agreementId,
        dealerId: agreementId,
        externalId: orderNumber.toString(),
        orderStatus: "PENDING",
        orderSource: "PORTAL",
        cid: "",
        reference: "",
        locale: "nb-no",
        orderLines: [],
        totalAmount: amount,
        orderDate: draft.orderDate,
        paymentDueDate: draft.dueDate,
        paymentMethods: [                                                                                                                 
            {
                id: uuid(),
                paymentType: "INVOICE",
                invoiceSentByMerchant: draft.isEhf ?? false,
                invoiceDocumentType: "PDF",
                totalPaymentAmountIncTax: amount
            }
        ],
        attachments: draft.attachments.map((attachment) => ({
            id: uuid(),
            name: attachment.name as string,
            contentType: attachment.contentType as string,
            content: attachment.content as string,
            contentExtension: attachment.extension as string
        })),
        customerNotifications: [],
        hooks: [],
        shippingAddress: {
            id: uuid(),
            firstName: contactFirstName,
            lastName: contactLastName,
            legalId: draft.debtorOrgNo,
            companyName: draft.debtorName,
            companyType: "",
            email: draft.contactEmail ?? "",
            phone: draft.contactPhone ?? "",
            addressLine1: "",
            addressLine2: "",
            zipCode: "",
            city: "",
            country: "",
            countryCode: "",
        },
        billingAddress: {
            id: uuid(),
            firstName: "",
            lastName: "",
            legalId: draft.debtorOrgNo,
            companyName: draft.debtorName,
            companyType: "",
            email: draft.approverEmail ?? "",
            phone: "",
            addressLine1: "",
            addressLine2: "",
            zipCode: "",
            city: "",
            country: "",
            countryCode: "",
        }
    }
}

export const SellInvoicePage = () => {

    // Setup API clients
    const {createOrder} = useContext(OrderContext)
    const {activeAgreementId} = useContext(AgreementContext)

    const [order, setOrder] = useState<OrderDraft>({})
    const [error, setError] = useState<Error | undefined>()
    const [success, setSuccess] = useState<string | undefined>()
    const [isCreatingOrder, setIsCreatingOrder] = useState(false)

    const [activeStep, setActiveStep] = useState(0)


    const onSelectCustomer = (customerOrgNo: string, customerName: string) => {
        setOrder({ ...order, debtorOrgNo: customerOrgNo, debtorName: customerName })
    }

    const onConfirmCustomer = () => {
        setActiveStep(1)
    }

    const onUploadFile = (files: any[]) => {
        console.info(`Did upload ${files.length} files`)
        setOrder({ ...order, attachments: files })
    }

    const onScanResult = (scanResult?: ScanResult) => {
        console.info("Did finish scanning invoice file.")
        if (!scanResult) return setActiveStep(2)
        const dateParts = scanResult.dueDate.split(".")
        const dueDate = dateParts.length == 2 ? new Date(parseInt(dateParts[2]), parseInt(dateParts[1]), parseInt(dateParts[0])) : undefined
        // Use functional setState, to prevent this state update to overwrite attachments from onUploadFile
        setOrder(prev => {
            return {
                ...prev,
            orderNumber: parseInt(scanResult.invoiceNumber),
            dueDate: dueDate?.toISOString() ?? "",
            amount: scanResult.amount
            }

        })
        setActiveStep(2)
    }

    const onConfirmOrderData = (data: any) => {
        setOrder({ 
            ...order, 
            orderNumber: data.invoiceNumberValue,
            dueDate: data.dueDateValue,
            orderDate: data.invoiceDateValue,
            amount: data.amountValue,
            isEhf: data.isEHF
        })
        setActiveStep(3)
    }

    const onConfirmContactInfo = (contactInfo: ContactInfoProps) => {
        console.info("Contact info: ", contactInfo)
        const retOrder = {...order, ...contactInfo}
        setOrder(retOrder)
        setActiveStep(4)
    }

    const onConfirmOrder = async () => {
        console.info("Order: ", order)
        if (activeAgreementId == undefined) return
        const newOrder = mapData(order, activeAgreementId)
        console.info("New order: ", newOrder)
        setIsCreatingOrder(true)
        try {
            await createOrder(newOrder)
            setSuccess("Ok!")
            setActiveStep(0)
            setOrder({})
        } catch (err: any) {
            console.error("Failed to create order: ", err)
            setError(err)
        } finally {
            setIsCreatingOrder(false)
        }
    }

    const onPrevious = () => {
        setActiveStep(Math.max(activeStep - 1, 0))
    }
    

    return (
        <PageContainer variant="outlined">
            <Box sx={{ maxWidth: 600, margin: "24px auto" }} component="form" >
                { error && <Alert severity="error" sx={{margin: "10px 0px"}}>Kunne ikke opprette faktura.</Alert> }
                { !error && success && <Alert severity="success">Faktura er lagt opp i våre systemer.</Alert>}
                <Stepper activeStep={activeStep} orientation="vertical">
                    <Step key={0}>
                        <StepLabel>Kundens navn</StepLabel>
                        <StepContent>
                            <CustomerInfoStep onSelectCustomer={onSelectCustomer} onConfirmCustomer={onConfirmCustomer}/>
                        </StepContent>
                    </Step>
                    <Step key={1}>
                        <StepLabel>Last opp ordredokument</StepLabel>
                        <StepContent>
                            <UploadDocumentStep 
                                onUploadFile={onUploadFile} 
                                onScanComplete={onScanResult}
                                onCancel={onPrevious}
                            />
                        </StepContent>
                    </Step>
                    <Step key={2}>
                        <StepLabel>Fakturainfo</StepLabel>
                        <StepContent>
                            <InvoiceSummaryStep 
                                invoiceDate={order.dueDate}
                                amount={order.amount}
                                dueDate={order.dueDate}
                                onConfirmOrderData={onConfirmOrderData}
                                onCancel={onPrevious}
                            />
                        </StepContent>
                    </Step>
                    <Step key={3}>
                        <StepLabel>Kontaktinfo</StepLabel>
                        <StepContent>
                            <ContactInfoStep onConfirmContactInfo={onConfirmContactInfo} onCancel={onPrevious} showBillingInfo={!order.isEhf ?? true}/>
                        </StepContent>
                    </Step>
                    <Step key={4}>
                        <StepLabel>Bekreft</StepLabel>
                        <StepContent>
                            <ConfirmOrderStep 
                                draft={order} 
                                onConfirmOrder={onConfirmOrder} 
                                onCancelOrder={onPrevious} 
                                isCreatingOrder={isCreatingOrder}
                            />
                        </StepContent>
                    </Step>
                </Stepper>
            </Box>
        </PageContainer>
    )
}

interface CustomerInfoStepProps {
    onSelectCustomer: (customerOrgNo: string, customerName: string) => void
    onConfirmCustomer: () => void
}   

const CustomerInfoStep: React.FC<CustomerInfoStepProps> = ({onSelectCustomer, onConfirmCustomer}) => {
    const [companyName, setCompanyName] = useState<string | undefined>(undefined)

    const onSelectCompany = (company: {name: string, organizationNumber: string} | undefined) => {
        if (company) {
            console.info("Did select company", company)
            setCompanyName(company.name)
            onSelectCustomer(company.organizationNumber,  company.name)
        }
    }

    return (
        <Stack spacing={3}>
            <Typography variant="body1">Søk opp din kunde. Du kan bare selge fakturaer som er utstedt til bedrifter.</Typography>
            <CompanySearch onSelectCompany={onSelectCompany} clearOnSelect={false} />
            <Button variant="contained" disabled={!companyName} onClick={onConfirmCustomer}>{!companyName ? `Neste` : `Selg faktura til ${companyName}`}</Button>
        </Stack>
    )
}

interface ScanResult {
    accountNumber: string
    amount: string
    cid: string
    dueDate: string,
    invoiceNumber: string
}

interface UploadDocumentStepProps {
    onUploadFile: (files: any[]) => void
    onScanComplete: (scanResult?: ScanResult) => void
    onCancel: () => void
}

const UploadDocumentStep: React.FC<UploadDocumentStepProps> = ({onUploadFile, onScanComplete, onCancel}) => {
    const [error, setError] = useState<Error | undefined>(undefined)
    const {acceptedFiles, getRootProps, getInputProps} = useDropzone({
        maxSize: 10485760,
        accept: "application/pdf",
        onDrop(acceptedFiles, fileRejections, event) {
            fileRejections.forEach((file) => {
                file.errors.forEach((err) => {
                    if (err.code === "file-too-large") {
                        setError(new Error("Opplastet fil er for stor. Dersom fakturaen inneholder store bilder kan du forsøke å fjerne disse. Den kan også sendes inn per e-post."))
                    } else if (err.code === "file-invalid-type") {
                        setError(new Error("Filtypen støttes ikke."))
                    }
                })
            })
        },
    });
    const [isScaning, setIsScaning] = useState(false)

    const scanInvoiceDocument = async (file: string) => {
        setIsScaning(true)
        const requestBody = {
            "content": file,
            "fileName": "faktura.pdf",
            "fileExtension": ".pdf"
        }
        const scanResponse = await fetch("https://invoice-interpreter-prod01.azurewebsites.net/invoice/scan", {
          method: "POST",
          body: JSON.stringify(requestBody),
        })
        if (!scanResponse.ok) {
            console.error("Failed scaning!")
            setIsScaning(false)
            onScanComplete()
            return
        }

        const json = await scanResponse.json()
        const data : ScanResult = {
            invoiceNumber: json.invoiceNumber?.value ?? "",
            accountNumber: json.accountNumber?.value ?? "",
            amount: json.amount?.value ?? "",
            cid: json.cid?.value ?? "",
            dueDate: json.dueDate?.value ?? ""
        }
        setIsScaning(false)
        onScanComplete(data)
    }

    const onSubmit = async () => {
        const files = acceptedFiles.map(async (file) => {
            const base64File = await toBase64(file)
            return {
                id: uuid(),
                name: file.name,
                content: base64File,
                contentExtension: file.name.slice(file.name.indexOf("."), file.name.length),
                contentType: "application/pdf",
            }
        })
        const base64Files = await Promise.all(files)
        onUploadFile(base64Files)
        base64Files.forEach(file => scanInvoiceDocument(file.content))
    }

    return (
        <>
            {error && <Alert severity="error">{error.message}</Alert> }
            <Dropzone {...getRootProps({className: 'dropzone'})}>
                <input {...getInputProps()}  />
                { acceptedFiles.length === 0 ? 
                <p>Klikk for å laste opp ordre, eller dra og slipp ordrefil.</p> : 
                <FileNameContainer><FilePresent /> {acceptedFiles[0].name}</FileNameContainer> }
            </Dropzone>
            <Button onClick={onCancel}>Forrige</Button>
            <LoadingButton 
                loading={isScaning}
                variant="contained"
                disabled={acceptedFiles.length === 0}
                sx={{marginTop: 1}}
                onClick={onSubmit}
            >Neste</LoadingButton>
        </>
    )
}

interface InvoiceSummaryStepProps {
    invoiceNumber?: string
    amount?: string
    invoiceDate?: string
    dueDate?: string,
    onConfirmOrderData: (info: any) => void
    onCancel: () => void
}

const InvoiceSummaryStep: React.FC<InvoiceSummaryStepProps> = ({
    invoiceNumber, 
    amount, 
    invoiceDate, 
    dueDate, 
    onConfirmOrderData,
    onCancel
}) => {
    const [invoiceNumberValue, setInvoiceNumberValue] = useState<string>(invoiceNumber || "")
    const [amountValue, setAmountValue] = useState<string>(amount || "")
    const [invoiceDateValue, setInvoiceDateValue] = useState(invoiceDate || null)
    const [dueDateValue, setDueDateValue] = useState(dueDate || null)
    const [isEHF, setIsEHF] = useState(false)

    const onChangeInvoiceNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInvoiceNumberValue(e.target.value)
    }

    const onChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAmountValue(e.target.value)
    }

    const onChangeIsEHF = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setIsEHF(checked)
    }

    const onConfirmOrderInfo = () => {
        console.info("Did confirm order info")
        onConfirmOrderData({invoiceNumberValue, amountValue, invoiceDateValue, dueDateValue, isEHF})
    }

    console.log(invoiceDateValue)
    const enableButton = invoiceNumberValue.length > 0 
        && amountValue.length > 3
        && invoiceDateValue !== null
        && dueDateValue !== null

    return (
        <>
        <TextField sx={{margin: "8px"}} label="Fakturanummer" value={invoiceNumberValue} onChange={onChangeInvoiceNumber}/>
        <TextField sx={{margin: "8px"}} label="Beløp (inkl mva.)" value={amountValue} onChange={onChangeAmount}/>
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Fakturadato"
              value={invoiceDateValue}
              onChange={(newValue) => {
                setInvoiceDateValue(newValue);
              }}
              renderInput={(params) => <TextField sx={{margin: "8px"}} {...params} />}
            />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Forfallsdato"
              value={dueDateValue}
              onChange={(newValue) => {
                setDueDateValue(newValue);
              }}
              renderInput={(params) => <TextField sx={{margin: "8px"}} {...params} />}
            />
        </LocalizationProvider>
        <FormGroup>
            <FormControlLabel control={<Checkbox sx={{margin: 1}} value="isEHF" onChange={onChangeIsEHF}/>} label="Faktura er sendt som EHF." />
        </FormGroup>
        <Button onClick={onCancel}>Forrige</Button>
        <Button variant="contained" disabled={!enableButton} onClick={onConfirmOrderInfo}>Neste</Button>
        </>
    )
}

interface ContactInfoProps {
    contactName?: string,
    contactEmail?: string,
    contactPhone?: string,
    approverName?: string,
    approverEmail?: string,
}

interface ContactInfoStepProps {
    contactInfo?: ContactInfoProps
    onConfirmContactInfo: (info: ContactInfoProps) => void
    onCancel: () => void
    showBillingInfo: boolean
}

const ContactInfoStep: React.FC<ContactInfoStepProps> = (props) => {

    const [contactInfo, setContactInfo] = useState<ContactInfoProps | undefined>(props.contactInfo)
    const [hasApprover, setHasApprover] = useState<boolean>(false)

    const enableButton = contactInfo?.contactName != undefined
        && contactInfo.contactName.length > 2
        && contactInfo.contactEmail != undefined
        && contactInfo.contactEmail.length > 2

    const onChangeContactName =  (e: React.ChangeEvent<HTMLInputElement>) => {
        setContactInfo({
            ...contactInfo,
            contactName: e.target.value
        })
    }

    const onChangeContactEmail =  (e: React.ChangeEvent<HTMLInputElement>) => {
        setContactInfo({
            ...contactInfo,
            contactEmail: e.target.value
        })
    }

    const onChangeApproverName =  (e: React.ChangeEvent<HTMLInputElement>) => {
        setContactInfo({
            ...contactInfo,
            approverName: e.target.value
        })
    }

    const onChangeApproverEmail =  (e: React.ChangeEvent<HTMLInputElement>) => {
        setContactInfo({
            ...contactInfo,
            approverEmail: e.target.value
        })
    }

    const onChangeContactPhone =  (e: React.ChangeEvent<HTMLInputElement>) => {
        setContactInfo({
            ...contactInfo,
            contactPhone: e.target.value
        })
    }

    const onChangeHasApprover = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setHasApprover(checked)
    }

    const onConfirm = () => {
        console.info("Did confirm contact info")
        props.onConfirmContactInfo(contactInfo!)
    }

    const onCancel = () => {
        props.onCancel()
    }

    return (
        <>
            <Typography sx={{marginTop: 2, marginBottom: 2}} variant="body1">Legg inn kundens kontaktinformasjon.</Typography>
            <Typography variant="h6">Kontaktperson</Typography>
            <TextField sx={{margin: 1}} label="Navn" value={contactInfo?.contactName} onChange={onChangeContactName} />
            <TextField sx={{margin: 1}} label="E-post" value={contactInfo?.contactEmail} onChange={onChangeContactEmail} />
            <TextField sx={{margin: 1}} label="Telefon" value={contactInfo?.contactPhone} onChange={onChangeContactPhone} />
            { props.showBillingInfo && (
                <>
                {/*<FormGroup>
                    <FormControlLabel control={<Checkbox sx={{margin: 1}} value="hasApprover" onChange={onChangeHasApprover}/>} label="Legg til fakturamottak" />
                </FormGroup>
                { hasApprover ?
                <> */ }
                    <Typography variant="h6">Fakturamottak</Typography>
                    <div>
                        <TextField sx={{margin: 1}} label="Navn" value={contactInfo?.approverName} onChange={onChangeApproverName} />
                        <TextField sx={{margin: 1}} label="E-post" value={contactInfo?.approverEmail} onChange={onChangeApproverEmail} />
                    </div> 
                { /*</> : undefined 
                } */}
            </>
            )}
            <Stack direction="row">
                <Button onClick={onCancel}>Forrige</Button>
                <Button variant="contained" disabled={!enableButton} onClick={onConfirm}>Neste</Button>
            </Stack>
        </>
    )
}

interface ConfirmOrderStepProps {
    draft?: OrderDraft
    onCancelOrder: () => void
    onConfirmOrder: () => void
    isCreatingOrder: boolean
}

const ConfirmOrderStep: React.FC<ConfirmOrderStepProps> = ({draft, onCancelOrder, onConfirmOrder, isCreatingOrder}) => {

    const onCancel = () => {
        onCancelOrder()
    }

    const onConfirm = () => {
        onConfirmOrder()
    }

    const orderDate = draft && draft.orderDate && (draft.orderDate as any).format("DD.MM.YYYY")
    const dueDate = draft && draft.dueDate && (draft.dueDate as any).format("DD.MM.YYYY")
    return (
        <>
            <Stack direction="column" spacing={2} sx={{padding: "16px 16px", backgroundColor: "aliceblue"}}>
                <ListItem label="Kunde" value={draft?.debtorName ?? ""}/>
                <ListItem label="Beløp" value={draft?.amount ?? ""}/>
                <ListItem label="Fakturanummer" value={draft?.orderNumber?.toString() ?? ""}/>
                <ListItem label="Fakturadato" value={orderDate ?? ""}/>
                <ListItem label="Forfallsdato" value={dueDate ?? ""}/>
                <ListItem label="Kontaktperson" value={draft?.contactName ?? ""}/>
                <ListItem label="E-post (kontaktperson)" value={draft?.contactEmail ?? ""}/>
                <ListItem label="Tlf. (kontaktperson)" value={draft?.contactPhone ?? ""}/>
                {draft?.approverEmail && <ListItem label="E-post (fakturamottak)" value={draft?.approverEmail ?? ""}/> }
                <ListItem label="Sendt som EHF" value={draft?.isEhf ? "Ja" : "Nei"}/>
            </Stack>
            <div style={{marginTop: "16px"}}>
                <Button onClick={onCancel}>Forrige</Button>
                <LoadingButton variant="contained" loading={isCreatingOrder} onClick={onConfirm}>Bekreft</LoadingButton>
            </div>
        </>
    )
}

interface ListItemProps {
    label: string
    value: string
}

const ListItem: React.FC<ListItemProps> = ({label, value}) => {
    return (
        <Stack direction="row"spacing={2}>
            <Typography variant="body1">{label}:</Typography>
            <Typography variant="body2" alignSelf={"center"}>{value}</Typography>
        </Stack>
    )
}